import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Resource, Action } from '../Constants/socket';
import useSocketEvent from '../Hooks/useSocketEvent';
import useSocketSubscription from '../Hooks/useSocketSubscription';

const ExportImportProgressContext = createContext();

export const useExportImportProgress = () => {
    const context = useContext(ExportImportProgressContext);
    if (!context) {
        throw new Error('useExportImportProgress must be used within ExportImportProgressProvider');
    }
    return context;
};

export const ExportImportProgressProvider = ({ children }) => {
    const [tasks, setTasks] = useState([]);

    // Subscribe to all active task IDs with fromTimestamp for missed event replay
    const exportTasks = tasks.filter(t => t.type === 'export');
    const importTasks = tasks.filter(t => t.type === 'import');
    const exportIds = exportTasks.map(t => t.id);
    const importIds = importTasks.map(t => t.id);

    // Use earliest serverCreatedAt so the server replays any events emitted
    // between task creation and subscription establishment (race condition fix)
    const exportFromTimestamp = exportTasks.length > 0
        ? exportTasks.reduce((earliest, t) => (!earliest || t.serverCreatedAt < earliest) ? t.serverCreatedAt : earliest, null)
        : null;
    const importFromTimestamp = importTasks.length > 0
        ? importTasks.reduce((earliest, t) => (!earliest || t.serverCreatedAt < earliest) ? t.serverCreatedAt : earliest, null)
        : null;

    useSocketSubscription(Resource.DATA_EXPORT, exportIds.length > 0 ? exportIds : null, false, exportFromTimestamp);
    useSocketSubscription(Resource.DATA_IMPORT, importIds.length > 0 ? importIds : null, false, importFromTimestamp);

    // Add a new task to track
    const addTask = useCallback((task) => {
        setTasks(prev => {
            // Avoid duplicates
            if (prev.find(t => t.id === task.id)) {
                return prev;
            }
            return [...prev, {
                ...task,
                createdAt: Date.now(),
                serverCreatedAt: task.serverCreatedAt || null,
                status: task.status || 'processing',
                progress: task.progress ?? 0
            }];
        });
    }, []);

    // Update an existing task
    const updateTask = useCallback((id, updates) => {
        setTasks(prev => prev.map(task =>
            task.id === id ? { ...task, ...updates } : task
        ));
    }, []);

    // Remove a task
    const removeTask = useCallback((id) => {
        setTasks(prev => prev.filter(task => task.id !== id));
    }, []);

    // Handle export updates from socket
    const handleExportUpdate = useCallback((data) => {
        if (!data?.id) return;

        setTasks(prev => {
            const existingTask = prev.find(t => t.id === data.id && t.type === 'export');
            if (!existingTask) return prev;

            // Extract status from the status object
            let status = 'processing';
            let downloadUrl = existingTask.downloadUrl;

            if (data.status && typeof data.status === 'object' && data.status.message_code) {
                const parts = data.status.message_code.split('.');
                status = parts[parts.length - 1]; // e.g., 'completed', 'failed'
            }

            if (status === 'completed' && data.download_url) {
                downloadUrl = data.download_url;
            }

            return prev.map(task =>
                task.id === data.id
                    ? {
                        ...task,
                        status,
                        progress: data.progress ?? task.progress,
                        downloadUrl,
                        statusObj: data.status
                    }
                    : task
            );
        });
    }, []);

    // Handle import updates from socket
    const handleImportUpdate = useCallback((data) => {
        if (!data?.id) return;

        setTasks(prev => {
            const existingTask = prev.find(t => t.id === data.id && t.type === 'import');
            if (!existingTask) return prev;

            // Extract status similar to exports
            let status = 'processing';

            if (data.status && typeof data.status === 'object' && data.status.message_code) {
                const parts = data.status.message_code.split('.');
                status = parts[parts.length - 1];
            }

            return prev.map(task =>
                task.id === data.id
                    ? {
                        ...task,
                        status,
                        progress: data.progress ?? task.progress,
                        statusObj: data.status
                    }
                    : task
            );
        });
    }, []);

    // Listen to socket events
    useSocketEvent(Resource.DATA_EXPORT, Action.UPDATE, handleExportUpdate);
    useSocketEvent(Resource.DATA_IMPORT, Action.UPDATE, handleImportUpdate);

    // Auto-remove completed/failed tasks after 15 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setTasks(prev => prev.filter(task => {
                if (task.status === 'completed' || task.status === 'failed') {
                    const age = Date.now() - task.createdAt;
                    return age < 15000; // Remove after 15 seconds
                }
                return true; // Keep processing tasks
            }));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <ExportImportProgressContext.Provider value={{ tasks, addTask, updateTask, removeTask }}>
            {children}
        </ExportImportProgressContext.Provider>
    );
};
